import { useEmbarcacaoSistemasDp } from "@/modules/embarcacoes";
import type { EmbarcacaoSistemasDp, EmbarcacaoSistemasDpField } from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import { formatDetailDate } from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";

export const SISTEMAS_DP_ID = "sistemas-dp";
export const SISTEMAS_DP_TITLE = "Sistemas de DP";
export const SISTEMAS_DP_LOADING_MESSAGE = "Carregando os Sistemas de DP da embarcação...";

/** Padrão global de estado vazio do Hub para a seção sem informações (AC3). */
const SISTEMAS_DP_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação de Sistemas de DP disponível",
  description:
    "Assim que houver informações de DP para esta embarcação, elas aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-sistemas-dp";

interface SistemasDpFieldDefinition {
  readonly key: EmbarcacaoSistemasDpField;
  readonly label: string;
  /** Tratamento de apresentação do valor; sem ele o valor vai como recebido. */
  readonly format?: (value: string) => string;
}

/**
 * Os 5 campos da seção, na ordem e nas colunas do protótipo (AC2). O grid os
 * distribui em duas colunas, preenchendo a linha da esquerda para a direita.
 *
 * A data recebe apenas o formato de leitura do Hub — nenhum valor é calculado
 * ou derivado (AC2).
 */
export const SISTEMAS_DP_FIELDS: SistemasDpFieldDefinition[] = [
  { key: "dp", label: "DP" },
  { key: "fabricanteModelo", label: "Fabricante e Modelo" },
  { key: "sistemaReferenciaRelativo1", label: "Sistema Referência Relativo 1" },
  { key: "sistemaReferenciaRelativo2", label: "Sistema Referência Relativo 2" },
  { key: "atualizacaoDp", label: "Data Atualização DP", format: formatDetailDate },
];

/** Valor do campo pronto para a tela; `null` segue como campo sem preenchimento. */
function fieldValue(
  sistemasDp: EmbarcacaoSistemasDp,
  { key, format }: SistemasDpFieldDefinition,
): string | null {
  const value = sistemasDp[key];
  if (value === null) return null;

  return format ? format(value) : value;
}

/**
 * Seção Sistemas de DP do detalhamento da embarcação (US-1799).
 *
 * Exclusivamente de leitura (RN003): nenhum botão ou campo de cadastro, edição
 * ou exclusão. Os valores são apresentados como recebidos da fonte, com os
 * únicos tratamentos previstos — data no padrão do Hub e "Campo sem
 * preenchimento" para valor ausente (AC2/AC3).
 */
export function SistemasDpSection({
  embarcacaoId,
  onReauthenticate,
}: EmbarcacaoDetailSectionProps) {
  const { status, sistemasDp, errorMessage, reload } = useEmbarcacaoSistemasDp(embarcacaoId);

  return (
    <DetailSection
      id={SISTEMAS_DP_ID}
      title={SISTEMAS_DP_TITLE}
      // Só há data de atualização a exibir quando a seção tem dados.
      ultimaAtualizacao={sistemasDp ? sistemasDp.ultimaAtualizacao : undefined}
      testId={TEST_ID}
    >
      {sistemasDp ? (
        <DetailSectionFields testId={`${TEST_ID}-fields`}>
          {SISTEMAS_DP_FIELDS.map((field) => (
            <DetailField
              key={field.key}
              label={field.label}
              value={fieldValue(sistemasDp, field)}
              testId={`${TEST_ID}-${field.key}`}
            />
          ))}
        </DetailSectionFields>
      ) : (
        <DetailSectionState
          // `success` sempre traz os campos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={SISTEMAS_DP_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={SISTEMAS_DP_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
